import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { ClipboardCheck, ArrowDownLeft, Ban, Clock3, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const WITHHOLD_REASONS = [
  { value: "damage", label: "Damage" },
  { value: "fuel_shortage", label: "Fuel/charge shortage" },
  { value: "late_return", label: "Late return" },
  { value: "cleaning", label: "Cleaning" },
  { value: "missing_item", label: "Missing item/key" },
  { value: "unpaid_balance", label: "Unpaid balance" },
  { value: "policy_violation", label: "Policy violation" },
  { value: "other", label: "Other" },
];

const peso = (n) => `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Phase 2H (spec F): a lightweight "what happened at return, now resolve
 * the deposit" nudge. Deliberately thin: the four outcomes here call the
 * exact same backend endpoints DepositPanel.jsx uses (refund/withhold/
 * mark-refund-pending): no separate resolution endpoint, no duplicated
 * lifecycle logic. For anything more nuanced than these four shapes, staff
 * drops down to the full DepositPanel below.
 *
 * Renders only when there's actually something to resolve (deposit
 * received or already refund_pending, with a remaining balance), so it's
 * useful both right after a return inspection finalizes AND any time later
 * a staff member revisits the booking with an open deposit.
 */
export default function ResolveDepositPanel({ bookingId, onChanged, highlight }) {
  const [deposit, setDeposit] = useState(null);
  const [returnContext, setReturnContext] = useState(null);
  const [mode, setMode] = useState(null); // 'partial' | 'withhold_full'
  const [reason, setReason] = useState("damage");
  const [note, setNote] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/bookings/${bookingId}/deposit`);
      setDeposit(data);
    } catch (_) { /* no permission: stay hidden */ }
    try {
      const { data } = await api.get(`/bookings/${bookingId}/inspections`);
      setReturnContext(data.by_type?.return || null);
    } catch (_) { /* ignore */ }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [bookingId]);

  if (!deposit || deposit.remaining_held <= 0) return null;
  if (!["received", "refund_pending"].includes(deposit.status)) return null;

  const counts = returnContext?.checklist_counts || {};
  const hasDamage = (counts.damage || 0) + (counts.missing || 0) > 0;

  const act = async (path, body, success) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${bookingId}/deposit/${path}`, body);
      toast.success(success);
      setMode(null);
      setNote("");
      setRefundAmount("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const refundFull = () => act("refund", { amount: deposit.remaining_held }, "Full refund recorded");
  const decideLater = () => act("mark-refund-pending", {}, "Marked as refund pending");

  const withholdFull = () => {
    if (reason === "other" && !note.trim()) {
      toast.error("A note is required when the reason is 'Other'.");
      return;
    }
    act("withhold", { amount: deposit.remaining_held, reason, note: note.trim() || undefined },
        "Full withholding recorded");
  };

  const refundPartially = async () => {
    const amount = Number(refundAmount);
    if (!amount || amount <= 0 || amount > deposit.remaining_held) {
      toast.error(`Enter a refund amount up to ${peso(deposit.remaining_held)}.`);
      return;
    }
    if (reason === "other" && !note.trim()) {
      toast.error("A note is required when the withholding reason is 'Other'.");
      return;
    }
    const withheldAmount = Math.round((deposit.remaining_held - amount) * 100) / 100;
    setBusy(true);
    try {
      if (withheldAmount > 0.01) {
        await api.post(`/bookings/${bookingId}/deposit/withhold`,
          { amount: withheldAmount, reason, note: note.trim() || undefined });
      }
      await api.post(`/bookings/${bookingId}/deposit/refund`, { amount });
      toast.success("Partial refund recorded");
      setMode(null); setNote(""); setRefundAmount("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`rounded-xl border p-5 ${highlight ? "border-[#1B4332] ring-2 ring-[#1B4332]/20 bg-emerald-50/40" : "border-slate-200 bg-white"}`}
         data-testid="resolve-deposit-panel">
      <h2 className="font-display text-base font-semibold inline-flex items-center gap-2">
        <ClipboardCheck className="w-4 h-4 text-[#1B4332]" /> Resolve deposit
      </h2>
      {returnContext && (
        <p className="text-xs text-slate-600 mt-1" data-testid="resolve-deposit-context">
          Return inspection completed
          {hasDamage ? ": possible damage or missing items flagged." : ": no damage flagged."}
          {returnContext.fuel_level != null && <> Fuel/charge at return: {returnContext.fuel_level}%.</>}
        </p>
      )}
      <p className="text-sm text-slate-700 mt-2">
        Deposit received: <span className="font-semibold">{peso(deposit.remaining_held)}</span> still held
      </p>

      {!mode && (
        <div className="flex flex-wrap gap-2 mt-3">
          <QuickBtn icon={ArrowDownLeft} label={`Refund full (${peso(deposit.remaining_held)})`}
                    onClick={refundFull} busy={busy} testId="resolve-deposit-refund-full" />
          <QuickBtn icon={ChevronRight} label="Refund partially"
                    onClick={() => setMode("partial")} busy={busy} testId="resolve-deposit-refund-partial" />
          <QuickBtn icon={Ban} label="Withhold fully" danger
                    onClick={() => setMode("withhold_full")} busy={busy} testId="resolve-deposit-withhold-full" />
          <QuickBtn icon={Clock3} label="Decide later"
                    onClick={decideLater} busy={busy} testId="resolve-deposit-decide-later" />
        </div>
      )}

      {mode === "withhold_full" && (
        <ReasonForm
          amountLabel={`Withholding ${peso(deposit.remaining_held)} in full`}
          reason={reason} setReason={setReason} note={note} setNote={setNote}
          onCancel={() => setMode(null)} onSubmit={withholdFull} busy={busy}
          testIdPrefix="resolve-deposit-withhold-full" />
      )}

      {mode === "partial" && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="resolve-deposit-partial-form">
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-500">
              Refund amount (max {peso(deposit.remaining_held)})
            </span>
            <input type="number" value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)}
                   data-testid="resolve-deposit-partial-amount"
                   className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          </label>
          <p className="text-xs text-slate-600">
            Remainder ({peso(Math.max(0, deposit.remaining_held - (Number(refundAmount) || 0)))}) will be withheld.
          </p>
          <ReasonFields reason={reason} setReason={setReason} note={note} setNote={setNote}
                        testIdPrefix="resolve-deposit-partial" />
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setMode(null)} className="px-3 py-1.5 text-sm rounded-md hover:bg-slate-100">Cancel</button>
            <button onClick={refundPartially} disabled={busy}
                    data-testid="resolve-deposit-partial-submit"
                    className="px-3 py-1.5 text-sm rounded-md bg-[#1B4332] text-white hover:opacity-90 disabled:opacity-50">
              {busy ? "Saving…" : "Confirm split"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReasonFields({ reason, setReason, note, setNote, testIdPrefix }) {
  return (
    <>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-slate-500">Withholding reason</span>
        <select value={reason} onChange={(e) => setReason(e.target.value)}
                data-testid={`${testIdPrefix}-reason`}
                className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm">
          {WITHHOLD_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-slate-500">
          Note {reason === "other" ? "(required)" : "(optional)"}
        </span>
        <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                  data-testid={`${testIdPrefix}-note`}
                  className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
      </label>
    </>
  );
}

function ReasonForm({ amountLabel, reason, setReason, note, setNote, onCancel, onSubmit, busy, testIdPrefix }) {
  return (
    <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 space-y-2" data-testid={`${testIdPrefix}-form`}>
      <div className="text-sm font-semibold text-red-900">{amountLabel}</div>
      <ReasonFields reason={reason} setReason={setReason} note={note} setNote={setNote} testIdPrefix={testIdPrefix} />
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded-md hover:bg-red-100">Cancel</button>
        <button onClick={onSubmit} disabled={busy}
                data-testid={`${testIdPrefix}-submit`}
                className="px-3 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
          {busy ? "Saving…" : "Confirm"}
        </button>
      </div>
    </div>
  );
}

function QuickBtn({ icon: Icon, label, onClick, busy, danger, testId }) {
  return (
    <button onClick={onClick} disabled={busy} data-testid={testId}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm disabled:opacity-50 ${
              danger ? "bg-white border border-red-300 text-red-700 hover:bg-red-50"
                     : "bg-white border border-slate-300 hover:bg-slate-50"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}
